import {
  VARIANCE_REPORT_MODE,
  VarianceReportModeType,
} from "constants/financialClosing";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useState } from "react";
import { useGetVarianceExplanationQuery } from "store/apis/reports";
import { VarianceExplanation } from "types/Models/report";
import { ReportFilterType } from "../ReportFilter";
import { ReportSummaryCard } from "./ReportSummaryCard";

type ReportSummaryBannerProps = {
  reportType: string;
  filter: ReportFilterType;
  mode: VarianceReportModeType;
  regenerateCount: number;
  period: string;
  storedOverview?: VarianceExplanation;
};

export const ReportSummaryBanner = ({
  reportType,
  filter,
  mode,
  regenerateCount,
  period,
  storedOverview,
}: ReportSummaryBannerProps) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const [hasRequestedRetry, setHasRequestedRetry] = useState(false);

  const hasStoredOverview = Boolean(storedOverview);
  const isRegenerating = mode === VARIANCE_REPORT_MODE.REGENERATE;
  const isGenerating = mode === VARIANCE_REPORT_MODE.GENERATE;
  const shouldFetch = isGenerating || isRegenerating || hasRequestedRetry;

  const { data, isFetching, isLoading, isError, refetch } =
    useGetVarianceExplanationQuery({
      groupId,
      entityId,
      categoryUuid: null,
      syntheticRowName: null,
      reportType,
      startDate: filter.START_DATE || null,
      endDate: filter.END_DATE || null,
      accountingMethod: filter.ACCOUNTING_METHOD,
      comparisonReportType: filter.COMPARE_BY,
      showNonZeroAccounts: filter.HIDE_ZERO,
      forceRegenerate: isRegenerating || hasRequestedRetry,
      regenerateCount,
    });

  const summary = data ?? storedOverview;
  const isMissing = !hasStoredOverview && !shouldFetch;
  const handleRetry = () => {
    if (isMissing) {
      setHasRequestedRetry(true);
      return;
    }
    refetch();
  };

  return (
    <ReportSummaryCard
      isLoading={isLoading || isFetching}
      isError={Boolean(isError)}
      summary={summary}
      period={period}
      onRetry={handleRetry}
    />
  );
};
